import { useEffect, useTransition } from "react";
import {
  useContainerDimensionsObserver,
  useFocusRange,
  usePnlPopupTimeRange,
  useSetPnlPopupTimeRange,
  useTrendChartBinSize,
} from "../../../../../utils/customHooks/genericHooks";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { SpringUpDown } from "components/Animations/SpringUpDown";
import useWindowSize from "utils/customHooks/useWindowSize";
import { PnlChartPopupContent } from "./PnlChartPopupContent";
import { useLocation } from "react-router";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { BREAKPOINT_DESKTOP } from "constants/constants";
import { ArrowBackIos } from "@mui/icons-material";
import styled, { css } from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useKey } from "react-use";
import { clamp } from "lodash";

type PnlChartPopupProps = {
  containerWidth?: number | null;
  portalContainerSelector?: string;
};
/**
 * Pnl Chart Popup, pops up over the Trend Chart when clicking a Trend Chart bar.
 * You may pass a `portalContainerSelector` CSS selector string
 * if you want to position the popup over top of an element instead of the whole page.
 * @param portalContainerSelector if you pass a selector string, the portal will be created in this element
 */
const PnlChartPopup = ({
  containerWidth: containerWidthProp,
  portalContainerSelector,
}: PnlChartPopupProps) => {
  const isStorybook = useIsStorybook();
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const setPnlPopupTimeRange = useSetPnlPopupTimeRange();
  const [, startTransition] = useTransition();
  const handleCloseModal = () => {
    startTransition(() => {
      setPnlPopupTimeRange(null);
    });
  };

  const open = Boolean(pnlPopupTimeRange);

  // close the popup when we navigate to a different page
  const { pathname } = useLocation();

  useEffect(
    handleCloseModal,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  const { innerWidth } = useWindowSize();

  // * bug? hublet view datepickerandcharts keeps overflow: hidden after closing popup, hack-fix here
  useEffect(() => {
    if (!open) {
      const datePickerAndCharts =
        document.querySelector<HTMLElement>(`.datepickerAndCharts`);
      if (datePickerAndCharts) {
        datePickerAndCharts.style.overflow = "";
      }
    }
  }, [open]);

  const { width: portalContainerSelectedWidth } =
    useContainerDimensionsObserver(portalContainerSelector ?? "_") ?? 0;
  const containerWidth =
    (portalContainerSelector ? portalContainerSelectedWidth : null) ??
    containerWidthProp ??
    innerWidth;
  const portalContainerElement = portalContainerSelector
    ? document.querySelector(portalContainerSelector)
    : null;
  return (
    <Modal
      disableScrollLock={true}
      // if we get a ref, use it as the modal container,
      // and apply `position: absolute` instead of `position: fixed` to the modal + backdrop.
      {...(portalContainerElement && !isStorybook
        ? {
            container: portalContainerElement,
            style: {
              position: "absolute",
              pointerEvents: open ? "auto" : "none",
              marginRight: innerWidth > BREAKPOINT_DESKTOP ? -12 : 0,
            },
            BackdropProps: {
              style: { position: "absolute" },
            },
          }
        : { style: { pointerEvents: open ? "auto" : "none" } })}
      open={open}
      onClose={handleCloseModal}
      componentsProps={{
        backdrop: {
          style: { marginRight: -16, paddingRight: 16 },
        },
      }}
      closeAfterTransition={true}
    >
      <SpringUpDown
        in={open}
        style={
          // if we get a ref, make the SpringUpDown container sized as 100% of its container height
          // (need display flex for this, according to StackOverflow, for weird CSS reasons.)
          portalContainerElement
            ? { height: "100%", display: "flex", pointerEvents: "none" }
            : {}
        }
      >
        {open && (
          <PopupAndControlsStyles $containerWidth={containerWidth}>
            <BtnPrev />
            <PnlChartPopupContent
              // the popup fits either its container width or the full window width
              containerWidth={containerWidth}
              isFullPagePopup={!portalContainerElement}
            />
            <BtnNext />
          </PopupAndControlsStyles>
        )}
      </SpringUpDown>
    </Modal>
  );
};

const BtnPrev = () => {
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const setPnlPopupTimeRange = useSetPnlPopupTimeRange();
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const { translate } = useFlightTranslation();
  const focusRange = useFocusRange();
  const prevRange = {
    ge: (pnlPopupTimeRange?.ge ?? 0) - trendChartBinSizeMs,
    lt: (pnlPopupTimeRange?.lt ?? 0) - trendChartBinSizeMs,
  };
  const isPrevRangeWithinFocusRange =
    focusRange &&
    pnlPopupTimeRange &&
    focusRange.ge <= prevRange.ge &&
    prevRange.lt <= focusRange.lt;
  const prevRangeClamped = {
    // trend chart bars are clamped at focus range
    ge: clamp(prevRange.ge, focusRange?.ge ?? 0, focusRange?.lt ?? 0),
    lt: clamp(prevRange.lt, focusRange?.ge ?? 0, focusRange?.lt ?? 0),
  };
  const handlePrev = () => {
    if (isPrevRangeWithinFocusRange) {
      setPnlPopupTimeRange(prevRangeClamped);
    }
  };
  useKey("ArrowLeft", handlePrev, {}, [
    prevRangeClamped,
    isPrevRangeWithinFocusRange,
  ]);
  const { innerWidth } = useWindowSize();

  return innerWidth <= 1000 ? null : (
    <Tooltip title={translate("Previous")}>
      <IconButton
        size="large"
        onClick={handlePrev}
        style={{
          // use visibility: hidden instead of removing the elements, to keep the layout stable
          visibility: isPrevRangeWithinFocusRange ? "visible" : "hidden",
        }}
      >
        <ArrowBackIos />
      </IconButton>
    </Tooltip>
  );
};
const BtnNext = () => {
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const setPnlPopupTimeRange = useSetPnlPopupTimeRange();
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const { translate } = useFlightTranslation();
  const focusRange = useFocusRange();
  const nextRange = {
    ge: (pnlPopupTimeRange?.ge ?? 0) + trendChartBinSizeMs,
    lt: (pnlPopupTimeRange?.lt ?? 0) + trendChartBinSizeMs,
  };
  const isNextRangeWithinFocusRange =
    focusRange &&
    pnlPopupTimeRange &&
    focusRange.ge <= nextRange.ge &&
    nextRange.lt <= focusRange.lt;
  const nextRangeClamped = {
    // trend chart bars are clamped at focus range
    ge: clamp(
      (pnlPopupTimeRange?.ge ?? 0) + trendChartBinSizeMs,
      focusRange?.ge ?? 0,
      focusRange?.lt ?? 0
    ),
    lt: clamp(
      (pnlPopupTimeRange?.lt ?? 0) + trendChartBinSizeMs,
      focusRange?.ge ?? 0,
      focusRange?.lt ?? 0
    ),
  };

  const handleNext = () => {
    if (isNextRangeWithinFocusRange) {
      setPnlPopupTimeRange(nextRangeClamped);
    }
  };
  useKey("ArrowRight", handleNext, {}, [
    nextRangeClamped,
    isNextRangeWithinFocusRange,
  ]);
  const { innerWidth } = useWindowSize();
  return innerWidth <= 1000 ? null : (
    <Tooltip title={translate("Next")}>
      <IconButton
        size="large"
        onClick={handleNext}
        style={{
          // use visibility: hidden instead of removing the elements, to keep the layout stable
          visibility: isNextRangeWithinFocusRange ? "visible" : "hidden",
        }}
      >
        <ArrowBackIos sx={{ transform: "rotate(180deg)" }} />
      </IconButton>
    </Tooltip>
  );
};

const PopupAndControlsStyles = styled.div<{ $containerWidth: number | null }>`
  display: flex;
  align-items: center;
  /* gap: 10px; */
  .MuiIconButton-root {
    height: fit-content;
    color: white;
    pointer-events: auto;
  }
  position: relative;
  [aria-label="Previous"],
  [aria-label="Next"] {
    position: absolute;
  }
  [aria-label="Previous"] {
    left: 0px;
  }
  [aria-label="Next"] {
    right: 0px;
  }
  ${props =>
    900 < props.$containerWidth && props.$containerWidth <= 1000
      ? css`
          [aria-label="Previous"] {
            left: -12px;
          }
          [aria-label="Next"] {
            right: -12px;
          }
        `
      : 1000 < props.$containerWidth
        ? css`
            [aria-label="Previous"] {
              left: -48px;
            }
            [aria-label="Next"] {
              right: -48px;
            }
          `
        : ""}
  @media (min-width: ${BREAKPOINT_DESKTOP}px) {
    [aria-label="Previous"] {
      left: 0px;
    }
    [aria-label="Next"] {
      right: 0px;
    }
  }
`;

export default PnlChartPopup;
